import { z } from 'zod'

import { nanoidSchema } from '@/utility'

import { nanoid } from '../../utility'
import { projectIconSchema } from '../icon'

export type Children = string[]

export enum SidebarType {
  Page = 'Page',
  Folder = 'Folder',
  Link = 'Link',
}

const baseSidebarItemSchema = z
  .object({
    uid: nanoidSchema,
    title: z.string().trim().min(1),
    type: z.enum(SidebarType),
    link: z.string().optional(),
    icon: projectIconSchema.optional(),
    children: z.string().array().optional(),
    description: z.string().optional(),
    show: z.boolean(),
  })
  .meta({ id: 'base-sidebar-item' })

/** Base sidebar item. Links, Pages, and Folders will all extend this schema */
export type BaseSidebarItem = z.infer<typeof baseSidebarItemSchema>

// ---------------------------------------------------------------------------

const linkSchema = baseSidebarItemSchema
  .extend({
    title: z.preprocess(
      (arg) => (arg as string)?.trim() || 'Link',
      z.string().trim().min(1),
    ),
    link: z.string(),
    type: z.literal(SidebarType.Link),
  })
  .meta({ id: 'sidebar-link' })

/** Link sidebar item */
export type Link = z.infer<typeof linkSchema>

// ---------------------------------------------------------------------------

const pageSchema = baseSidebarItemSchema
  .extend({
    title: z.preprocess(
      (arg) => (arg as string)?.trim() || 'Page',
      z.string().trim().min(1),
    ),
    sidebarTitle: z.string().optional(),
    description: z.string(),
    children: z.string().array().default([]),
    type: z.literal(SidebarType.Page),
    defaultOpen: z.boolean().optional(),
    yjsReference: z.string(),
    backgroundImage: z.string().optional(),
    icon: projectIconSchema.optional(),
    /**
     * Automatically set during sync builds when we map markdown and mdx files to pages
     *
     * Used by the Vue builds to determine how to render a given page item.
     */
    isMdx: z.boolean().default(false),
    /**
     * Automatically set during sync builds when we map HTML files to pages.
     */
    isHtml: z.boolean().default(false),
  })
  .meta({ id: 'sidebar-page' })

/** Page sidebar item */
export type Page = z.infer<typeof pageSchema>

export const defaultPage = (): Page => ({
  uid: nanoid(),
  title: 'Getting Started',
  type: SidebarType.Page,
  description: 'Start writing your API Documentation with our platform',
  children: [],
  show: true,
  yjsReference: nanoid(),
  isMdx: false,
  isHtml: false,
})

// ---------------------------------------------------------------------------

const folderSchema = baseSidebarItemSchema
  .extend({
    title: z.preprocess(
      (arg) => (arg as string)?.trim() || 'Folder',
      z.string().trim().min(1),
    ),
    children: z.string().array().default([]),
    type: z.literal(SidebarType.Folder),
    defaultOpen: z.boolean().optional(),
  })
  .meta({ id: 'sidebar-folder' })

/** Folder sidebar item */
export type Folder = z.infer<typeof folderSchema>

// ---------------------------------------------------------------------------

export const sidebarItemSchema = z
  .union([linkSchema, pageSchema, folderSchema])
  .meta({ id: 'sidebar-item' }) satisfies z.ZodType<SidebarItem>

export type SidebarItem = Link | Page | Folder

// ---------------------------------------------------------------------------

export const sidebarSchema = z
  .object({
    /** Complete record of ALL sidebar items */
    items: z.record(z.string(), sidebarItemSchema),
    /** Ordered list of the direct children */
    children: z.string().array(),
  })
  .overwrite((val) => {
    // Ensure at least 1 page exists for a guide
    const hasPage = Object.values(val.items).some(
      (item) => item.type === SidebarType.Page,
    )
    if (!hasPage) {
      const firstPage = defaultPage()
      val.items[firstPage.uid] = firstPage
      val.children.push(firstPage.uid)
    }

    return val
  })
  .meta({ id: 'sidebar' })

export type Sidebar = z.infer<typeof sidebarSchema>
